import iro from "@jaames/iro";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";

import { usePalettePreview } from "../api";

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** The value once typing pauses, so the preview is not asked for every key. */
function useSettled(value: string, delay = 150) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/**
 * A thematic's custom colour: an iro.js colour wheel (the Django form's own)
 * and a hexadecimal field kept in sync, with a preview of the colours both apps
 * derive from it. The preview comes from the server, which also refuses a
 * colour no text is readable on; the warning says so before saving.
 */
export function ColourPicker({
  id,
  value,
  onChange,
  invalid = false,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  const wheel = useRef<HTMLDivElement>(null);
  const picker = useRef<iro.ColorPicker | null>(null);
  const change = useRef(onChange);
  change.current = onChange;

  useEffect(() => {
    const host = wheel.current;
    if (!host) return;
    const instance = iro.ColorPicker(host, {
      width: 180,
      color: HEX.test(value) ? value : "#ffffff",
      layout: [
        { component: iro.ui.Wheel },
        { component: iro.ui.Slider, options: { sliderType: "value" } },
      ],
    });
    instance.on("input:change", (color: iro.Color) => {
      change.current(color.hexString.toUpperCase());
    });
    picker.current = instance;
    return () => {
      picker.current = null;
      host.replaceChildren();
    };
    // The wheel is built once; the field below moves it afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const current = picker.current;
    if (
      current &&
      HEX.test(value) &&
      current.color.hexString.toUpperCase() !== value.toUpperCase()
    ) {
      current.color.hexString = value;
    }
  }, [value]);

  const settled = useSettled(value.trim());
  const preview = usePalettePreview(HEX.test(settled) ? settled : null);
  const colours = HEX.test(value.trim()) ? preview.data : undefined;

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-4 rounded-md border border-border p-4">
      <div ref={wheel} aria-hidden data-testid="colour-wheel" />
      <div className="min-w-0 flex-1 basis-56 space-y-1.5">
        <Input
          id={id}
          value={value}
          maxLength={7}
          placeholder="#C2410C"
          spellCheck={false}
          autoComplete="off"
          aria-invalid={invalid || undefined}
          className="w-36 font-mono uppercase"
          onChange={(event) => onChange(event.target.value)}
        />
        <p className="text-xs text-muted-foreground">{t("catalogue.colour.hint")}</p>
        {colours && (
          <div className="flex flex-wrap items-center gap-2 pt-1" aria-live="polite">
            <span className="text-xs text-muted-foreground">{t("catalogue.colour.preview")}</span>
            {/* Server-derived hex values: no token exists for a colour picked here. */}
            <span
              className="rounded px-2.5 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: colours.fill, color: colours.on_fill }}
            >
              {t("catalogue.colour.fill")}
            </span>
            {/* On white in both themes: the ink is derived for a light background. */}
            <span
              className="rounded border border-border bg-white px-2.5 py-0.5 text-xs font-semibold"
              style={{ color: colours.ink }}
            >
              {t("catalogue.colour.ink")}
            </span>
            <span
              className="rounded px-2.5 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: colours.soft, color: colours.ink }}
            >
              {t("catalogue.colour.soft")}
            </span>
          </div>
        )}
        {colours && !colours.readable && (
          <p
            role="status"
            className="rounded-md border border-warning/40 bg-warning/10 p-2.5 text-sm text-foreground"
          >
            {t("catalogue.colour.unreadable")}
          </p>
        )}
      </div>
    </div>
  );
}
